import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useCustomerContacts } from "@/hooks/use-customer-contacts";
import { useCompanyHolidays } from "@/hooks/use-company-holidays";
import { useSendCustomerEmail } from "@/hooks/use-send-customer-email";
import { useGenerateCrmDraft, type CrmDraft } from "@/hooks/use-generate-crm-draft";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { isValidEmail } from "@/lib/email-validation";
import { describeSendTime } from "@/lib/send-window";
import { TONE_TEXT } from "@/lib/widget-status";
import ContactFormDialog from "./ContactFormDialog";
import type { DBCustomerContact } from "@/lib/db-types";

const MAX_CC = 3;

interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** Forhåndsvalgt kontaktperson (f.eks. hovedkontakten). */
  defaultContactId?: string | null;
  /** Svar i tråd: aktiviteten det svares på. */
  replyToActivityId?: string | null;
  defaultSubject?: string;
  /** Forhåndsfylt meldingstekst (f.eks. lagret mailutkast på en oppfølging). */
  defaultBody?: string;
  /** Kobler e-posten til prosessen og oppfølgingen den sendes fra. */
  opportunityId?: string | null;
  followUpId?: string | null;
  /** Kalles med aktivitet-id etter at e-posten er sendt eller planlagt, før dialogen lukkes. */
  onSent?: (activityId: string) => void;
  /**
   * Mailutkast i Sølvrev-tone (CRM-2.8). `autoGenerate` lager utkast når dialogen
   * åpnes uten forhåndsfylt tekst; `onGenerated` lar kalleren cache utkastet.
   */
  draft?: { autoGenerate?: boolean; onGenerated?: (draft: CrmDraft) => void };
}

/** Kommaseparert cc-felt til en liste; ugyldige adresser gir feilmelding i stedet for å forsvinne. */
function parseCc(raw: string): { list: string[]; error: string | null } {
  const list = raw.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
  const bad = list.find((e) => !isValidEmail(e));
  if (bad) return { list, error: `Ugyldig cc-adresse: ${bad}` };
  if (list.length > MAX_CC) return { list, error: `Maks ${MAX_CC} cc-adresser` };
  return { list, error: null };
}

/**
 * «Send e-post» på kundekortet. Sendes som den innloggede selgeren via Gmail;
 * utenfor sendevinduet (helg, rød dag, før 08:00) planlegges meldingen, og
 * dialogen sier når den går.
 */
export default function SendEmailDialog({
  open,
  onOpenChange,
  customerId,
  defaultContactId,
  replyToActivityId,
  defaultSubject = "",
  defaultBody = "",
  opportunityId,
  followUpId,
  onSent,
  draft,
}: SendEmailDialogProps) {
  const { user: authUser } = useAuth();
  const { isImpersonating } = useImpersonation();
  const { data: contacts = [] } = useCustomerContacts(customerId);
  const send = useSendCustomerEmail();
  const draftsEnabled = useFeatureFlag("feature_crm_ko_pipe");
  const generate = useGenerateCrmDraft();
  const autoTried = useRef(false);

  const year = new Date().getFullYear();
  const { data: holidaysThis } = useCompanyHolidays(year);
  const { data: holidaysNext } = useCompanyHolidays(year + 1);

  const sendable = useMemo(
    () => contacts.filter((c) => c.is_active && !c.anonymized_at && !!c.email),
    [contacts],
  );

  const [contactId, setContactId] = useState("");
  /** Ny eller redigert mottaker rett fra dialogen: "new", kontakten som redigeres, eller null. */
  const [contactForm, setContactForm] = useState<"new" | DBCustomerContact | null>(null);
  const selectedContact = sendable.find((c) => c.id === contactId) ?? null;
  const [subject, setSubject] = useState(defaultSubject);
  const [text, setText] = useState("");
  const [ccRaw, setCcRaw] = useState("");

  useEffect(() => {
    if (!open) return;
    const preferred = sendable.find((c) => c.id === defaultContactId) ?? sendable.find((c) => c.is_primary) ?? sendable[0];
    setContactId(preferred?.id ?? "");
    setSubject(defaultSubject);
    setText(defaultBody);
    setCcRaw("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultContactId, defaultSubject, defaultBody]);

  // Mangler valgt kontakt (listen lastet etter åpning), velg standard.
  useEffect(() => {
    if (open && !contactId && sendable.length > 0) {
      setContactId((sendable.find((c) => c.id === defaultContactId) ?? sendable.find((c) => c.is_primary) ?? sendable[0]).id);
    }
  }, [open, contactId, sendable, defaultContactId]);

  // Siste verdier for å se om brukeren har skrevet mens utkastet ble laget.
  const typed = useRef({ subject: "", text: "" });
  typed.current = { subject, text };
  // Økt-teller: øker når dialogen åpnes, så et utkast fra en tidligere økt ikke skriver over en ny.
  const session = useRef(0);
  useEffect(() => {
    if (open) session.current += 1;
  }, [open, replyToActivityId]);

  // mutateAsync (ikke mutate-callbacks) så utkastet caches selv om dialogen lukkes før svaret kommer.
  // `auto`: skriv aldri over det brukeren har begynt på.
  const handleGenerate = (auto = false) => {
    const startedIn = session.current;
    generate
      .mutateAsync({
        kind: replyToActivityId ? "reply" : "follow_up",
        customerId,
        contactId: contactId || null,
        followUpId: followUpId ?? null,
        threadActivityId: replyToActivityId ?? null,
        silent: auto,
      })
      .then((result) => {
        const untouched = !typed.current.text.trim() && !typed.current.subject.trim();
        const sameSession = startedIn === session.current;
        if (sameSession && (!auto || untouched)) {
          setSubject(result.subject);
          setText(result.body);
        }
        // Cache bare utkast som faktisk ble vist, så et brukerskrevet utkast ikke erstattes ved neste åpning.
        if (!auto || untouched) draft?.onGenerated?.(result);
      })
      .catch(() => undefined); // feilen vises som toast av hooken
  };

  // Auto-utkast én gang per åpning, bare når det ikke finnes tekst fra før.
  useEffect(() => {
    if (!open) {
      autoTried.current = false;
      return;
    }
    if (autoTried.current || !draft?.autoGenerate || !draftsEnabled || defaultBody || !contactId) return;
    autoTried.current = true;
    handleGenerate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft?.autoGenerate, draftsEnabled, defaultBody, contactId]);

  const cc = parseCc(ccRaw);
  const when = useMemo(() => {
    const holidays = new Set<string>([...(holidaysThis ?? []), ...(holidaysNext ?? [])]);
    return describeSendTime(new Date(), { startHour: 8, holidays, tz: "Europe/Oslo" });
    // Beregnes på nytt når dialogen åpnes, så teksten ikke er utdatert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, holidaysThis, holidaysNext]);

  const canGenerate = draftsEnabled && !!contactId && !generate.isPending && !send.isPending;
  const canSend = !!contactId && !!subject.trim() && !!text.trim() && !cc.error && !send.isPending;

  const handleSend = async () => {
    const result = await send.mutateAsync({
      customerId,
      contactId,
      subject: subject.trim(),
      text: text.trim(),
      cc: cc.list,
      opportunityId: opportunityId ?? null,
      followUpId: followUpId ?? null,
      inReplyToActivityId: replyToActivityId ?? null,
    });
    onSent?.(result.activity_id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send e-post</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSend) return;
            handleSend().catch(() => undefined);
          }}
        >
          <div className="space-y-4 py-2">
            {isImpersonating && (
              <p className={`text-sm ${TONE_TEXT.near}`}>
                Du sender som {authUser?.email ?? "deg selv"}, ikke som den du ser systemet som.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="send-email-contact">Til</Label>
              {sendable.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen kontaktpersoner med e-postadresse ennå.</p>
              ) : (
                <Select value={contactId} onValueChange={setContactId}>
                  <SelectTrigger id="send-email-contact" aria-label="Velg mottaker">
                    <SelectValue placeholder="Velg kontaktperson" />
                  </SelectTrigger>
                  <SelectContent>
                    {sendable.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex gap-3 text-sm">
                <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={() => setContactForm("new")}>
                  Ny kontaktperson
                </button>
                {selectedContact && (
                  <button type="button" className="font-medium underline-offset-4 hover:underline" onClick={() => setContactForm(selectedContact)}>
                    Rediger mottaker
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="send-email-cc">Kopi (valgfritt, maks {MAX_CC})</Label>
              <Input
                id="send-email-cc"
                value={ccRaw}
                onChange={(e) => setCcRaw(e.target.value)}
                placeholder="kollega@solvrev.no"
                aria-invalid={!!cc.error}
              />
              {cc.error && <p className={`text-xs ${TONE_TEXT.miss}`}>{cc.error}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="send-email-subject">Emne</Label>
              <Input id="send-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="send-email-text">Melding</Label>
                {draftsEnabled && (
                  <Button type="button" variant="outline" size="sm" onClick={() => handleGenerate()} disabled={!canGenerate}>
                    {generate.isPending ? "Lager utkast ..." : text.trim() ? "Lag nytt utkast" : replyToActivityId ? "Foreslå svar" : "Lag utkast"}
                  </Button>
                )}
              </div>
              <Textarea
                id="send-email-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder="Skriv meldingen her"
              />
            </div>

            <p className={`text-sm ${when.sendNow ? "text-muted-foreground" : TONE_TEXT.near}`} aria-live="polite">
              {when.label}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!canSend}>
              {send.isPending ? "Sender ..." : when.sendNow ? "Send e-post" : "Planlegg sending"}
            </Button>
          </DialogFooter>
        </form>
        {/* Utenfor e-postskjemaet: submit fra kontaktskjemaet skal ikke boble til sendingen. */}
        <ContactFormDialog
          open={contactForm !== null}
          onOpenChange={(o) => !o && setContactForm(null)}
          customerId={customerId}
          contact={contactForm === "new" ? null : contactForm}
          onSaved={(c) => setContactId(c.id)}
        />
      </DialogContent>
    </Dialog>
  );
}
